import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/app/PageHeader';
import { DataTable } from '@/components/app/DataTable';
import { ErrorState } from '@/components/app/ErrorState';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { humanizeError } from '@/lib/apiClient';
import {
  funnelObservabilityApi,
  type ApproachOutcomeCell,
} from '../api/funnelObservabilityApi';

export function ApproachOutcomesPage() {
  const [pendingCategory, setPendingCategory] = React.useState('fitness');
  const [category, setCategory] = React.useState('fitness');
  const [pendingMin, setPendingMin] = React.useState(0);
  const [minSelections, setMinSelections] = React.useState(0);

  const params = React.useMemo(
    () => ({ minSelections, limit: 200 }),
    [minSelections],
  );

  const q = useQuery({
    queryKey: ['ai-approach-outcomes', category, params],
    queryFn: () => funnelObservabilityApi.listApproachOutcomeCells(category, params),
    enabled: !!category,
    staleTime: 60_000,
  });

  const apply = () => {
    setCategory(pendingCategory.trim());
    setMinSelections(pendingMin);
  };

  const columns = React.useMemo<ColumnDef<ApproachOutcomeCell>[]>(
    () => [
      {
        accessorKey: 'archetypeId',
        header: 'Archetype',
        enableSorting: true,
      },
      {
        accessorKey: 'approachId',
        header: 'Approach',
        enableSorting: true,
      },
      {
        accessorKey: 'adaptationHash',
        header: 'Adaptation',
        cell: ({ row }) => (
          <code className="text-xs text-muted-foreground">
            {row.original.adaptationHash}
          </code>
        ),
      },
      {
        accessorKey: 'selections',
        header: 'Selections',
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.selections}</span>
        ),
        enableSorting: true,
      },
      {
        id: 'completion',
        header: 'Completion',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {(row.original.completionRate * 100).toFixed(0)}%
            <span className="ml-1 text-muted-foreground">({row.original.completions})</span>
          </span>
        ),
      },
      {
        id: 'abandonment',
        header: 'Abandonment',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {(row.original.abandonmentRate * 100).toFixed(0)}%
            <span className="ml-1 text-muted-foreground">({row.original.abandonments})</span>
          </span>
        ),
      },
      {
        accessorKey: 'weightDecay',
        header: 'Decay',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.weightDecay !== undefined
              ? row.original.weightDecay.toFixed(2)
              : '—'}
          </span>
        ),
      },
      {
        accessorKey: 'lastUpdated',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {new Date(row.original.lastUpdated).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Approach outcomes"
        description="Per-(archetype × approach × adaptation) cells. Composer scoring multiplies by (1 - weightDecay)."
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="space-y-1.5">
            <Label htmlFor="category-input">Category</Label>
            <Input
              id="category-input"
              value={pendingCategory}
              onChange={(e) => setPendingCategory(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && apply()}
              className="w-48"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="min-selections">Min selections</Label>
            <Input
              id="min-selections"
              type="number"
              min={0}
              value={pendingMin}
              onChange={(e) => setPendingMin(parseInt(e.target.value, 10) || 0)}
              className="w-28"
            />
          </div>
          <Button onClick={apply}>Apply</Button>
        </CardContent>
      </Card>

      {q.isError ? (
        <ErrorState
          title="Approach outcomes failed"
          description={humanizeError(q.error)}
        />
      ) : (
        <DataTable
          columns={columns}
          data={q.data?.cells ?? []}
          loading={q.isLoading}
          rowKey={(c) => c.cellKey}
          emptyLabel={`No outcomes recorded for "${category}"`}
          emptyDescription="Cells appear once phases finalize through the Composer path."
        />
      )}
    </div>
  );
}
